import type { IProps } from "./markdown.types";
import ReactMarkdown from "react-markdown";
import type { Options } from "react-markdown";
import classNames from "classnames";
import { forwardRef, useCallback, useMemo, useState } from "react";
import remarkGfm from "remark-gfm";
import { preprocessString, preserveNewlines } from "./markdown.utilities";
import { Box } from "../box";
import { Image } from "../image";
import { MARKDOWN_COMPONENTS } from "./markdown.components";
import { CLASSNAME_REGEX, STRONG_CLASSNAME_REGEX } from "./markdown.constants";
import { MarkdownContext, NO_FAILED_IMAGE_SOURCES } from "./markdown.context";
import { remarkPlaceholders } from "./remark-placeholders";
import styles from "./markdown.module.scss";

/** The remark plugins for a render with no placeholders. */
const REMARK_PLUGINS: Options["remarkPlugins"] = [remarkGfm];

/** The remark plugins for a render with placeholders. */
const REMARK_PLUGINS_WITH_PLACEHOLDERS: Options["remarkPlugins"] = [remarkGfm, remarkPlaceholders];

/**
 * Markdown component for rendering markdown strings with custom styling and tooltip support.
 * @returns {JSX.Element} The Markdown component.
 */
const Markdown = forwardRef<HTMLDivElement, IProps>(function Markdown(
    {
        textProps,
        listProps,
        imageProps,
        listItemProps,
        linkTarget = "_blank",
        markdownString,
        anchorProps,
        className,
        placeholders,
        variant = "default",
        ...otherProps
    },
    ref
) {
    const content = useMemo(() => {
        return preserveNewlines(preprocessString(markdownString));
    }, [markdownString]);

    // Held as module constants: a new array identity makes `ReactMarkdown` re-parse on every render.
    const remarkPlugins = placeholders?.length ? REMARK_PLUGINS_WITH_PLACEHOLDERS : REMARK_PLUGINS;

    const ConditionalImagePreview = useMemo(() => {
        if (imageProps && imageProps.preview) {
            return Image.Preview;
        }

        return Box;
    }, [imageProps]);

    // Image sources that have failed to load. Deliberately held here, outside the markdown components, so that the
    // failure survives anything that remounts the rendered markdown: a source that has failed once is never rendered
    // as an `<img>` again, so the browser cannot request it again.
    const [failedImageSources, setFailedImageSources] = useState<ReadonlySet<string>>(NO_FAILED_IMAGE_SOURCES);

    const reportImageFailure = useCallback((source: string) => {
        // Returning the same set for an already-known failure keeps repeat error events from re-rendering.
        setFailedImageSources((sources) => (sources.has(source) ? sources : new Set(sources).add(source)));
    }, []);

    const markdownContext = useMemo(
        () => ({
            anchorProps,
            failedImageSources,
            imageProps,
            linkTarget,
            listItemProps,
            listProps,
            placeholders,
            reportImageFailure,
            textProps,
        }),
        [
            anchorProps,
            failedImageSources,
            imageProps,
            linkTarget,
            listItemProps,
            listProps,
            placeholders,
            reportImageFailure,
            textProps,
        ]
    );

    const rootClassName = useMemo(() => classNames(styles["markdown"], className), [className]);

    return (
        <Box {...otherProps} className={rootClassName} data-variant={variant}>
            <ConditionalImagePreview>
                <MarkdownContext.Provider value={markdownContext}>
                    <ReactMarkdown components={MARKDOWN_COMPONENTS} remarkPlugins={remarkPlugins}>
                        {content}
                    </ReactMarkdown>
                </MarkdownContext.Provider>
            </ConditionalImagePreview>
        </Box>
    );
});

export { Markdown, CLASSNAME_REGEX, STRONG_CLASSNAME_REGEX };
